/**
 * LocationPrintersList (BLU-877, plan 84-08) — the printers registered at this
 * location: name, the file type each one declared, and when it was last seen.
 *
 * WHAT IT REPLACES, AND WHY IT IS READ-ONLY. The retired "Print file formats"
 * card asked an operator to declare what this floor's printer eats. That was a
 * per-LOCATION scalar, and it could never describe ThreadHeads HQ, which runs
 * eleven print PCs across two device contracts at one location. Nobody declares
 * it any more — the agent install IS the device — so the question an operator
 * opens Printing Files to ask is unchanged and the answer is simply reported.
 * There is deliberately nothing to configure here.
 *
 * THE DISTINCTION THE WHOLE PHASE TURNS ON. A row with no declared format is
 * NOT a Brother. It behaves as one, because `.arx4` is what every existing
 * producer writes — but "this PC says it is a Brother" and "nobody has told us
 * what this PC is" are different facts, and rendering the second as the first
 * is exactly the laundering of an assumption into a declaration that the
 * per-location setting used to do. Undeclared rows therefore read as an
 * ASSUMED default, in muted type, against their own locale key.
 *
 * A name seeded from the machine hostname is a REAL name and gets no
 * "unconfigured" treatment — the operator named it by installing it on that PC.
 * Only a genuinely absent name falls back to a placeholder label.
 */
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';

/** Locale-driven relative-ish stamp — a date is enough at this cadence. */
function formatLastSeen(iso: string | null, never: string): string {
  if (!iso) return never;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return never;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function LocationPrintersList() {
  const { t } = useTranslation('settings');
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id ?? null;

  const printersQuery = trpc.productionSettings.listLocationPrinters.useQuery(
    { locationId: locationId ?? '' },
    { enabled: !!locationId },
  );

  // The print-agent card above is org-wide (installer, port); this half is the
  // only per-location thing on it, so it says which floor it is describing
  // rather than inheriting the card's framing.
  if (!locationId) return null;

  if (printersQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-6" role="status" aria-busy="true">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
      </div>
    );
  }

  // A failed read must NEVER fall through to the empty state below: "no
  // printers at this location" is a CLAIM about the floor, and a network blip
  // is not entitled to make it.
  if (printersQuery.isError) {
    return (
      <Alert variant="destructive" className="mt-6">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>{t('production.locationPrinters.loadFailedTitle')}</AlertTitle>
        <AlertDescription className="flex items-center gap-2">
          {t('production.locationPrinters.loadFailedBody')}
          <Button variant="link" className="h-auto p-0" onClick={() => void printersQuery.refetch()}>
            <RefreshCw className="mr-1 h-3 w-3" aria-hidden="true" />
            {t('production.common.retry')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const data = printersQuery.data;
  // The fleet is the location OWNER's. Say we did not read it, rather than
  // showing an empty list that reads as a fact about somebody else's floor.
  if (!data?.readable) {
    return (
      <p className="mt-6 max-w-prose text-xs text-muted-foreground">
        {t('production.locationPrinters.notVisibleToThisOrg')}
      </p>
    );
  }

  const printers = data.printers;

  return (
    <div className="mt-6 space-y-3">
      <div className="space-y-1">
        <h4 className="text-sm font-medium">
          {t('production.locationPrinters.title', { location: selectedLocation?.name ?? '' })}
        </h4>
        <p className="max-w-prose text-xs text-muted-foreground">
          {t('production.locationPrinters.description')}
        </p>
      </div>

      {data.legacyCustomsFormat && (
        <p
          className="max-w-prose text-xs text-amber-700 dark:text-amber-400"
          role="status"
        >
          <span className="inline-flex items-start gap-1">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('production.locationPrinters.legacyDeclaration', {
              format: data.legacyCustomsFormat,
            })}
          </span>
        </p>
      )}

      {printers.length === 0 ? (
        <p className="max-w-prose text-sm text-muted-foreground">
          {t('production.locationPrinters.empty')}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-2.5 text-left font-medium">
                  {t('production.locationPrinters.columnPrinter')}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t('production.locationPrinters.columnFormat')}
                </th>
                <th className="px-4 py-2.5 text-left font-medium">
                  {t('production.locationPrinters.columnLastSeen')}
                </th>
              </tr>
            </thead>
            <tbody>
              {printers.map((printer, i) => (
                <tr key={`${printer.name ?? 'unnamed'}-${i}`} className="border-b last:border-b-0">
                  <td className="px-4 py-2.5">
                    {printer.name ?? (
                      <span className="text-muted-foreground">
                        {t('production.locationPrinters.unnamed')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {printer.format ? (
                      <span className="font-mono text-xs">.{printer.format}</span>
                    ) : (
                      // NOT ".arx4". This PC has told us nothing; it behaves as
                      // a Brother, and saying so plainly is the difference
                      // between reporting a fact and inventing one.
                      <span className="text-xs text-muted-foreground">
                        {t('production.locationPrinters.formatAssumed')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {formatLastSeen(
                      printer.lastSeenAt,
                      t('production.locationPrinters.neverSeen'),
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
